import React from "react"

import { format } from "date-fns"
import { CalendarDays, Clock } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"

interface TimeSlot {
  time: string
  fullDate: Date
}

interface VehicleCollectionTimeSelectorProps {
  selectedDate: Date | undefined
  availableTimesForDate: (date: Date) => TimeSlot[]
  onTimeSelect: (fullDate: Date) => void
}

const VehicleCollectionTimeSelector: React.FC<VehicleCollectionTimeSelectorProps> = ({
  selectedDate,
  availableTimesForDate,
  onTimeSelect,
}) => {
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Clock className="h-5 w-5 text-honeycomb" />
          Available Times
        </CardTitle>
      </CardHeader>
      <CardContent className="min-h-[300px]">
        {selectedDate ? (
          availableTimesForDate(selectedDate).length > 0 ? (
            <div className="space-y-3">
              <p className="mb-4 text-sm text-gray-600">
                {format(selectedDate, "EEEE, MMMM d, yyyy")}
              </p>
              <div className="grid grid-cols-1 gap-2">
                {availableTimesForDate(selectedDate).map((timeSlot, index) => (
                  <Button
                    key={index}
                    type="button"
                    variant={
                      timeSlot.fullDate.getTime() === selectedDate?.getTime()
                        ? "default"
                        : "outline"
                    }
                    className={cn(
                      "h-12 w-full justify-start text-left",
                      timeSlot.fullDate.getTime() === selectedDate?.getTime()
                        ? "bg-honeycomb text-white hover:bg-amber-500"
                        : "hover:bg-gray-50",
                    )}
                    onClick={() => onTimeSelect(timeSlot.fullDate)}
                  >
                    <Clock className="mr-2 h-4 w-4" />
                    {timeSlot.time}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <Clock className="mb-3 h-12 w-12 text-gray-300" />
              <p className="text-gray-500">No times available for this date</p>
            </div>
          )
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <CalendarDays className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-gray-500">Please select a date first</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default VehicleCollectionTimeSelector
